import { TABLE_DESIGN_TAB_ID } from './../table-design-tab/table-design-tab';
import { TABLE_LAYOUT_TAB_ID } from './../table-layout-tab/table-tab';
import { HEADER_FOOTER_TAB_ID } from './../header-footer-tab/header-footer-tab';
import { HOME_TAB_ID } from './../home-tab/home-tab';
import { RIBBON_ID } from './ribbon-constants';
import { PICTURE_FORMAT_TAB_ID } from '../picture-format-tab/picture-format-tab';
import { isNullOrUndefined } from '@syncfusion/ej2-base';
/**
 * Manages contextual tabs in the ribbon
 * @private
 */
export class RibbonContextualTabManager {
    /**
     * Constructor for RibbonContextualTabManager
     * @param {DocumentEditorContainer} container - Document editor container
     */
    constructor(container) {
        /**
         * Flag to track if the current operation is content change
         * @private
         */
        this.isContentChange = false;
        this.container = container;
        this.ribbonDocumentEditor = this.container.ribbon;
    }
    /**
     * Set content change flag
     * @param {boolean} value - Flag value
     * @returns {void}
     * @private
     */
    setContentChangeFlag(value) {
        this.isContentChange = value;
    }
    /**
     * Get contextual tabs configuration
     * @returns {any[]} Array of contextual tab configurations
     * @private
     */
    getContextualTabs() {
        return [
            {
                tabs: [
                    this.ribbonDocumentEditor.tabManager.tableDesignTab.getTableDesignTab(),
                    this.ribbonDocumentEditor.tabManager.tableLayoutTab.getTableLayoutTab()
                ],
                visible: false
            },
            {
                tabs: [this.ribbonDocumentEditor.tabManager.headerFooterTab.getHeaderFooterTab()],
                visible: false
            },
            {
                tabs: [this.ribbonDocumentEditor.tabManager.pictureFormatTab.getPictureFormatTab()],
                visible: false
            }
        ];
    }
    /**
     * Update contextual tabs visibility based on selection
     * @param {Ribbon} ribbon - Ribbon instance
     * @returns {void}
     * @private
     */
    updateContextualTabs(ribbon) {
        if (!this.container.documentEditor || !this.container.documentEditor.selection ||
            (!isNullOrUndefined(this.container.documentEditor.selection) &&
                this.container.documentEditor.selection.contextType === this.ribbonDocumentEditor.previousContext &&
                this.container.documentEditor.selection.isEmpty) || (!isNullOrUndefined(this.container.documentEditor.editor)
            && this.container.documentEditor.editor.isInsertingTOC)) {
            return;
        }
        const currentContext = this.container.documentEditor.selection.contextType;
        const selectedTab = ribbon.tabs[ribbon.selectedTab].header;
        const ribbonId = this.container.element.id + RIBBON_ID;
        if ((selectedTab === this.container.ribbon.localObj.getConstant('Header & Footer')
            || selectedTab === this.container.ribbon.localObj.getConstant('Table Design')
            || selectedTab === this.container.ribbon.localObj.getConstant('Table Layout'))
            && !(currentContext === 'HeaderText' || currentContext === 'HeaderImage' || currentContext === 'HeaderTableText' || currentContext === 'HeaderTableImage'
                || currentContext === 'FooterText' || currentContext === 'FooterImage' || currentContext === 'FooterTableText' || currentContext === 'FooterTableImage')) {
            ribbon.selectTab(ribbonId + HOME_TAB_ID);
        }
        this.updateTableContextualTabs(ribbon, currentContext);
        this.updateHeaderFooterContextualTab(ribbon, currentContext);
        this.updatePictureFormatContextualTab(ribbon, currentContext);
        this.ribbonDocumentEditor.previousContext = currentContext;
    }
    updateTableContextualTabs(ribbon, currentContext) {
        const isTableVisible = currentContext.indexOf('Table') >= 0 && currentContext !== 'TableOfContents';
        const tableDesignId = this.container.element.id + RIBBON_ID + TABLE_DESIGN_TAB_ID;
        const tableLayoutId = this.container.element.id + RIBBON_ID + TABLE_LAYOUT_TAB_ID;
        if (isTableVisible) {
            let isTabPresent = false;
            // Check if the tab is already present in contextual tabs
            for (const contextualTab of ribbon.contextualTabs) {
                for (const tab of contextualTab.tabs) {
                    if ((tab.id === tableDesignId || tab.id === tableLayoutId) && contextualTab.visible) {
                        isTabPresent = true;
                        break;
                    }
                }
                if (isTabPresent) {
                    break;
                }
            }
            if (!isTabPresent) {
                ribbon.showTab(tableDesignId, true);
                ribbon.showTab(tableLayoutId, true);
                // Only select the tab if this is a content change (insert table)
                if (this.isContentChange) {
                    ribbon.selectTab(tableDesignId);
                    this.isContentChange = false;
                }
            }
            if (this.ribbonDocumentEditor.tabManager.tableDesignTab) {
                this.ribbonDocumentEditor.tabManager.tableDesignTab.onSelectionChange();
            }
            if (this.ribbonDocumentEditor.tabManager.tableLayoutTab) {
                this.ribbonDocumentEditor.tabManager.tableLayoutTab.onTableLayoutChange();
            }
        }
        else {
            ribbon.hideTab(tableDesignId, true);
            ribbon.hideTab(tableLayoutId, true);
        }
    }
    /**
     * hide the contextual tab
     * @param {EJ2Ribbon} ribbon - ribbon instance
     * @returns {void}
     * @private
     */
    hideContextualTab(ribbon) {
        if (this.container.documentEditor.selection &&
            this.container.documentEditor.selection.contextType === this.ribbonDocumentEditor.previousContext) {
            return;
        }
        const ribbonId = this.container.element.id + RIBBON_ID;
        const selectedTab = ribbon.tabs[ribbon.selectedTab].header;
        if (selectedTab === this.container.ribbon.localObj.getConstant('Header & Footer')
            || selectedTab === this.container.ribbon.localObj.getConstant('Table Design')
            || selectedTab === this.container.ribbon.localObj.getConstant('Table Layout')
            || selectedTab === this.container.ribbon.localObj.getConstant('Picture Format')) {
            ribbon.selectTab(ribbonId + HOME_TAB_ID);
        }
        ribbon.hideTab(ribbonId + TABLE_DESIGN_TAB_ID, true);
        ribbon.hideTab(ribbonId + TABLE_LAYOUT_TAB_ID, true);
        ribbon.hideTab(ribbonId + HEADER_FOOTER_TAB_ID, true);
        ribbon.hideTab(ribbonId + PICTURE_FORMAT_TAB_ID, true);
        // ribbon.selectTab(ribbonId + HOME_TAB_ID);
    }
    updateHeaderFooterContextualTab(ribbon, currentContext) {
        const isInHeaderFooter = currentContext.indexOf('Header') >= 0 || currentContext.indexOf('Footer') >= 0;
        const headerFooterTabId = this.container.element.id + RIBBON_ID + HEADER_FOOTER_TAB_ID;
        if (isInHeaderFooter) {
            let isTabPresent = false;
            // Check if the tab is already present in contextual tabs
            for (const contextualTab of ribbon.contextualTabs) {
                for (const tab of contextualTab.tabs) {
                    if (tab.id === headerFooterTabId && contextualTab.visible) {
                        isTabPresent = true;
                        break;
                    }
                }
                if (isTabPresent) {
                    break;
                }
            }
            if (!isTabPresent) {
                ribbon.showTab(headerFooterTabId, true);
                ribbon.selectTab(headerFooterTabId);
            }
        }
        else {
            ribbon.hideTab(headerFooterTabId, true);
        }
    }
    updatePictureFormatContextualTab(ribbon, currentContext) {
        const isImageSelected = currentContext.indexOf('Image') >= 0;
        const pictureFormatTab = this.container.element.id + RIBBON_ID + PICTURE_FORMAT_TAB_ID;
        if (isImageSelected) {
            let isTabPresent = false;
            // Check if the tab is already present in contextual tabs
            for (const contextualTab of ribbon.contextualTabs) {
                for (const tab of contextualTab.tabs) {
                    if (tab.id === pictureFormatTab && contextualTab.visible) {
                        isTabPresent = true;
                        break;
                    }
                }
                if (isTabPresent) {
                    break;
                }
            }
            if (!isTabPresent) {
                ribbon.showTab(pictureFormatTab, true);
                // Only select the tab if this is a content change (insert picture)
                if (this.isContentChange) {
                    ribbon.selectTab(pictureFormatTab);
                    this.isContentChange = false;
                }
                this.ribbonDocumentEditor.tabManager.pictureFormatTab.updateImageProperties();
            }
        }
        else {
            ribbon.hideTab(pictureFormatTab, true);
        }
    }
}
